import { useAuth0 } from '@auth0/auth0-react';
import { cn } from '@syncanix/ui';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import {
  PaletteActionsProvider,
  ShellCommandPalette,
  useCommandPalette,
  useGoToShortcuts,
} from '@/components/CommandPalette';
import { LegalFooter } from '@/components/LegalFooter/LegalFooter';
import { PaymentAlertBanner } from '@/components/PaymentAlertBanner';
import { Sidebar } from '@/components/Sidebar/Sidebar';
import { env } from '@/config/env';
import { useControlMode } from '@/core/providers/ControlModeProvider';
import { useT } from '@/core/providers/I18nProvider';
import { useTheme } from '@/core/providers/ThemeProvider';
import { useWorkspace } from '@/core/providers/WorkspaceProvider';
import { MaintenanceBanner } from '@/features/maintenance-banner';
import { purgeLegacyMapCache } from '@/features/map/atlas/legacy-cache-purge';
import { DashboardWidget } from '@/features/widget-embed';

import { MobileNavDrawer } from './MobileNavDrawer';
import {
  MOBILE_BREAKPOINT,
  isMobileViewport,
  readInitialCollapsed,
  writeCollapsed,
} from './shell-environment';
import { ShellTopBar } from './ShellTopBar';
import { useSidebarItems } from './use-sidebar-items';
import { RouteFallback } from '../router/RouteFallback';

import type { ReactElement } from 'react';

/**
 * Authenticated application chrome. Renders the sidebar on desktop, a
 * drawer on mobile (toggled from the TopBar), and the route outlet in the
 * main region. Persists desktop collapse state in localStorage so the user
 * experience matches across sessions.
 */
export function AppShell(): ReactElement {
  const { theme, toggleTheme } = useTheme();
  const { mode: controlMode, toggleMode: toggleControlMode } = useControlMode();
  const { workspaces, currentWorkspace, selectWorkspace } = useWorkspace();
  const { t } = useT();
  const navigate = useNavigate();
  const auth = useAuth0();

  // The localized, gated nav item list (build + gating extracted to a hook so
  // this shell stays under the A23 file-size ceiling — see use-sidebar-items).
  const sidebarItems = useSidebarItems();

  const [collapsed, setCollapsed] = useState<boolean>(() => readInitialCollapsed());
  const [drawerOpen, setDrawerOpen] = useState(false);
  // ⌘K / Ctrl+K jump-to launcher (Phase 10). Global so it opens from any page.
  const palette = useCommandPalette();
  // `g u` jump-to chord (UGL-105 A0); registered app-wide beside the ⌘K hotkey.
  useGoToShortcuts();
  const [mobile, setMobile] = useState<boolean>(() => isMobileViewport());
  // The topbar hamburger — MobileNavDrawer restores focus to it on close.
  const drawerTriggerRef = useRef<HTMLButtonElement>(null);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = (event: MediaQueryListEvent): void => {
      setMobile(event.matches);
      if (!event.matches) setDrawerOpen(false);
    };
    mql.addEventListener('change', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, []);

  const handleToggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writeCollapsed(next);
      return next;
    });
  }, []);

  // The retired WebGL map persisted layouts keyed by the customer's route +
  // endpoint inventory (F26). The atlas keeps positions in memory only, so
  // nothing writes that cache any more — this one-shot purge clears what an
  // OLD build may have left behind on this browser.
  useEffect(() => {
    purgeLegacyMapCache();
  }, []);

  const handleSignOut = useCallback(() => {
    const returnTo = env.VITE_MARKETING_URL ?? window.location.origin;
    void auth.logout({ logoutParams: { returnTo } });
  }, [auth]);

  const user = {
    name:
      auth.user?.name ?? auth.user?.nickname ?? auth.user?.email ?? t('appshell.defaultUserName'),
    email: auth.user?.email ?? '',
    picture: auth.user?.picture ?? null,
  };

  return (
    // The palette registry wraps the whole shell so the PAGE inside `<Outlet/>`
    // can publish commands to the one ⌘K palette rendered below it. Without this
    // seam a page that wants its own commands has to grow a second palette — and
    // two palettes both bound to ⌘K open on top of each other.
    <PaletteActionsProvider>
      {/* h-dvh + overflow-hidden pins the chrome to the viewport so the DOCUMENT
        never scrolls: the sidebar and the main region each own an independent
        scroll below. (Previously min-h-screen let a tall page grow the whole
        document — the topbar/sidebar scrolled away and the menu got hidden.) */}
      <div className="flex h-dvh flex-col overflow-hidden bg-background text-foreground">
        {/* Skip-to-content link (WCAG 2.4.1 Bypass Blocks): the first focusable
          element, visually hidden until focused, so a keyboard-only user can
          jump past the top bar + ~20 sidebar links straight to the main region.
          `focus:start-4` keeps it RTL-correct (logical inset, not `left`). */}
        <a
          href="#main-content"
          className="sr-only rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground focus:not-sr-only focus:absolute focus:start-4 focus:top-4 focus:z-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {t('appshell.skipToContent')}
        </a>
        <ShellTopBar
          mobile={mobile}
          collapsed={collapsed}
          drawerOpen={drawerOpen}
          drawerTriggerRef={drawerTriggerRef}
          onToggleDrawer={() => {
            setDrawerOpen((prev) => !prev);
          }}
          onToggleCollapse={handleToggleCollapse}
          sidebarItems={sidebarItems}
          onOpenPalette={() => {
            palette.setOpen(true);
          }}
          workspaces={workspaces}
          currentWorkspace={currentWorkspace}
          onSelectWorkspace={selectWorkspace}
          onCreateWorkspace={() => {
            void navigate('/onboarding/create-workspace');
          }}
          onAccount={() => {
            void navigate('/account');
          }}
          user={user}
          theme={theme}
          onToggleTheme={toggleTheme}
          controlMode={controlMode}
          onToggleControlMode={toggleControlMode}
          onSignOut={handleSignOut}
        />

        {/* D30 — global maintenance/incident banner; renders only when the public
          status feed reports an active or imminently-announced incident. */}
        <MaintenanceBanner />

        {/* C27 leg 3 — global payment-failed banner; renders only when the
          workspace subscription is past_due, hidden otherwise. */}
        <PaymentAlertBanner />

        {/* min-h-0: a flex child defaults to min-height:auto, which would let this
          row grow to its content and reintroduce document scroll. Releasing it
          lets the sidebar + main scroll within the fixed-height shell instead.

          Bedrock removed the SYN-30 `ContentBackdrop` (a brand-accent radial
          glow plus a 22px dot grid) that used to sit behind this row. Two
          reasons, both principle-level rather than taste: the dot grid puts
          1px marks all over the canvas, and Bedrock's whole structure is 1px
          hairlines — the texture and the rules compete, so the sheet stops
          reading as a sheet. And the glow is the accent used as a FILL, which
          principle 02 reserves the gradient (and the accent) away from. The
          canvas is now flat `background`, which is what the spec draws.

          `relative isolate` stays: the row is still the stacking context for
          the sticky save bars and drawers that pin inside it. */}
        <div className="relative isolate flex min-h-0 flex-1 overflow-hidden">
          {!mobile ? (
            <Sidebar
              items={sidebarItems}
              collapsed={collapsed}
              ariaLabel={t('appshell.navigation')}
            />
          ) : null}

          {mobile ? (
            <MobileNavDrawer
              open={drawerOpen}
              items={sidebarItems}
              onClose={closeDrawer}
              returnFocusRef={drawerTriggerRef}
            />
          ) : null}

          <main
            id="main-content"
            role="main"
            tabIndex={-1}
            className={cn(
              'flex min-h-0 flex-1 flex-col overflow-y-auto focus-visible:outline-none',
            )}
          >
            {/* `relative` makes this the positioning context for full-screen
              pages. A page with an `h-full` internal-scroll layout (the Widget
              Design Studio) fills it via `absolute inset-0`, so it is bounded to
              the shell height WITHOUT stretching this wrapper to content height —
              which previously pushed the studio's live-preview viewport ~3700px
              tall and rendered the widget off-screen (blank preview). Normal
              pages stay in-flow and grow as before (footer sits below them). */}
            <div className="relative flex-1">
              {/* A23: the lazy-route boundary lives INSIDE the shell so the
                chrome stays painted while a page chunk loads. */}
              <Suspense fallback={<RouteFallback />}>
                <Outlet />
              </Suspense>
            </div>
            <LegalFooter />
          </main>
        </div>

        {/* Phase 10 — the ⌘K/Ctrl+K jump-to launcher. Fed the same localized,
          gate-filtered nav items the sidebar renders (Labs, INTERNAL surface
          flags and RBAC alike), so it never offers a hidden destination — which
          is what keeps a flagged-off surface out of BOTH ways in. */}
        <ShellCommandPalette
          open={palette.open}
          onOpenChange={palette.setOpen}
          items={sidebarItems}
        />

        {/* Dogfood: Syncanix's own chat widget, acting as the signed-in user.
          Renders null + injects the loader client-side only; a pure no-op
          unless VITE_SYNCANIX_WIDGET_KEY is configured. */}
        <DashboardWidget />
      </div>
    </PaletteActionsProvider>
  );
}
